import { createRow, MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";
import { useParams } from "react-router";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
 
const FeuB7WindAssistedPropulsion = forwardRef(({ refetchMonitoringData, b7FuelWindData, b7WindAssistedPropulsionDropdown,vesselMstId }, ref) => {
    const RowActions = { Edit: 0, Add: 1 };
    const [activeAction, setActiveAction] = useState(-1);
    const [fuelWindData, setFuelWindData] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deletingRowIndex, setDeletingRowIndex] = useState();
 
    const [dropdownOptions, setDropdownOptions] = useState({ wapsTypes: [] });
 
    const reindexRefNumbers = (list) => list.map((row, i) => ({ ...row, wapsReferenceNo: String(i + 1) }));
 
    useEffect(() => {
        if (!b7WindAssistedPropulsionDropdown) return;
        const dropdownMap = [{ apiKey: "wapsTypes", stateKey: "wapsTypes" }];
        const formattedOptions = {};
 
        dropdownMap.forEach(({ apiKey, stateKey }) => {
            const list = b7WindAssistedPropulsionDropdown?.[apiKey] ?? [];
            formattedOptions[stateKey] = list
                .map((item) => ({
                    label: item?.value,
                    value: item?.code,
                }))
                .filter((option) => option.label && option.value);
        });
 
        setDropdownOptions((prev) => ({ ...prev, ...formattedOptions }));
    }, [b7WindAssistedPropulsionDropdown]);
 
    useEffect(() => {
        if (Array.isArray(b7FuelWindData)) {
            const transformed = b7FuelWindData.map((item) => ({
                wapsReferenceNumber: item?.wapsReferenceNumber || "",
                wapsType: item?.wapsType || "",
                wapsName: item?.wapsName || "",
                pwind: item?.pwind || "",
                pprop: item?.pprop || "",
                technicalDescription: item?.technicalDescription || "",
            }));
            setFuelWindData(reindexRefNumbers(transformed));
        }
    }, [b7FuelWindData]);
 
    const wapsTypeOptions = useMemo(() => dropdownOptions.wapsTypes || [], [dropdownOptions]);
    const wapsTypeLabelByCode = useMemo(
        () => Object.fromEntries(wapsTypeOptions.map((o) => [o.value, o.label])),
        [wapsTypeOptions]
    );
 
    const validateRequired = (value) => !!value?.length;
 
    const validateRow = (row) => {
        const errors = {};
        if (!validateRequired(row.wapsType)) errors.wapsType = "Field is required";
        return errors;
    };
 
    const resetActiveRow = () => {
        setActiveAction(-1);
        setValidationErrors({});
    };
 
    const handleDeleteBtnClick = (rowIndex) => {
        setConfirmDelete(true);
        setDeletingRowIndex(rowIndex);
    };
 
    const handleSave = async (data, table, silent = false, isAuto) => {
        const finalPayload = reindexRefNumbers(data);
        try {
            const res = await callApi({ method: "POST", url: API_ROUTES.B7(vesselMstId), data: finalPayload });
            if (res?.isSuccess) {
                if (!silent) emitToasterMsg("success", "Table B.7", isAuto ? "WASP has been autosaved successfully" : "Record saved successfully");
                setFuelWindData(finalPayload);
                setConfirmDelete(false);
                resetActiveRow();
                table.setCreatingRow(null);
                table.setEditingRow(null);
                if (typeof refetchMonitoringData === "function") await refetchMonitoringData();
            } else {
                emitToasterMsg("error", "Table B.7", res?.errorMessage);
                setConfirmDelete(false);
            }
        } catch (err) {
            emitToasterMsg("error", "Table B.7", err?.errorMessage);
        }
    };
 
    useImperativeHandle(ref, () => ({
        async submitAndCheck() {
            const { creatingRow, editingRow } = table.getState();
            if (!creatingRow && !editingRow) return true;
            const mode = creatingRow ? "add" : "edit";
            const editedValues = creatingRow ? creatingRow?._valuesCache : editingRow?._valuesCache;
            let isDirty = false;
            if (editingRow && editedValues) {
                isDirty = Object.keys(editedValues).some(
                    (key) => editedValues[key] !== editingRow.original[key]
                );
            }
            const validationRes = validateRow(editedValues, mode, {});
            const hasErrors = Object.values(validationRes).some((err) => err);
            if (hasErrors) {
                setValidationErrors(validationRes);
                emitToasterMsg(
                    "warning",
                    "Validation Error",
                    "Please correct the highlighted fields before proceeding."
                );
                return false;
            }
            if (creatingRow) {
                await handleCreateRowSave({ values: editedValues, table }, true);
                return true;
            } else if (editingRow && isDirty) {
                await handleEditRowSave({ values: editedValues, table, row: editingRow }, true);
                return true;
            }
            if (!isDirty) {
                table.setEditingRow(null);
                return true;
            }
        }
    }));
 
 
    const handleCreateRowSave = async ({ values, table }, isAuto = false) => {
        const errors = validateRow(values);
        if (Object.keys(errors).length) return setValidationErrors(errors);
        const newRow = { ...values, wapsReferenceNo: String(fuelWindData.length + 1) };
        await handleSave([...fuelWindData, newRow], table, false, isAuto);
    };
 
 
    const handleEditRowSave = async ({ values, table, row }, isAuto = false) => {
        const errors = validateRow(values);
        if (Object.keys(errors).length) return setValidationErrors(errors);
        const updated = fuelWindData.map((item, i) => (i === row.index ? { ...item, ...values } : item));
        await handleSave(updated, table, false, isAuto);
    };
 
    const columns = useMemo(
        () => [
            { accessorKey: "wapsReferenceNumber", header: "WAPS Reference No.", muiEditTextFieldProps: { inputProps: { maxLength: 1000 } } },
            {
                accessorKey: "wapsType",
                header: "WAPS Type",
                editVariant: "select",
                editSelectOptions: wapsTypeOptions,
                Cell: ({ cell }) => wapsTypeLabelByCode[cell.getValue()] || cell.getValue(),
                muiEditTextFieldProps: {
                    required: true,
                    error: !!validationErrors?.wapsType,
                    helperText: validationErrors?.wapsType,
                    onFocus: () => setValidationErrors((prev) => ({ ...prev, wapsType: undefined })),
                    SelectProps: {
                        MenuProps: { disableScrollLock: true, PaperProps: { sx: { maxHeight: 250, boxShadow: 3 } } },
                    },
                },
            },
            { accessorKey: "wapsName", header: "WAPS Name", muiEditTextFieldProps: { inputProps: { maxLength: 1000 } } },
            { accessorKey: "pwind", header: "Pwind", muiEditTextFieldProps: { inputProps: { maxLength: 1000 } } },
            { accessorKey: "pprop", header: "Pprop", muiEditTextFieldProps: { inputProps: { maxLength: 1000 } } },
            { accessorKey: "technicalDescription", header: "Technical description of WAPS equipment", muiEditTextFieldProps: { inputProps: { maxLength: 1000 },borderBottom:"none"} },
        ],
        [validationErrors, wapsTypeOptions, wapsTypeLabelByCode]
    );
 
    const table = useMaterialReactTable({
        columns,
        data: fuelWindData,
        enableEditing: true,
        enablePagination: false,
        createDisplayMode: "row",
        editDisplayMode: "row",
        enableBottomToolbar:false ,
        onCreatingRowCancel: resetActiveRow,
        onEditingRowCancel: resetActiveRow,
        onCreatingRowSave: handleCreateRowSave,
        onEditingRowSave: handleEditRowSave,
 
        muiTableBodyProps: {
  sx: {
    "& tr:has(td[colspan]) td": {
      padding: 0,
      height: 0,
      border: "none",
    },
  },
},
 
 
muiTableContainerProps: {
  sx: {
    border: "1px solid lightgray",
    borderRadius: "1px",
    minHeight: "150px",
    maxHeight: "500px",
     boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
  },
},
 
 muiTablePaperProps: {
    sx: {
      border: "2px solid lightgray",
      borderRadius: "8px",
      boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
      padding: "8px 16px 16px",
      minHeight: "200px",
      display: "flex",
      flexDirection: "column",
    },
  },
 
 
        // ✅ Disable all unwanted UI elements
        enableColumnActions: false,
        enableColumnFilters: false,
        enableGlobalFilter: false,
        enableFilters: false,
        enableHiding: false,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        enableSorting: false,
 
        // ✅ White vertical line after each column
        muiTableBodyCellProps: ({ row, cell }) => ({
            sx: {
                borderRight: "1px solid lightgray",
                "&:last-child": {
                    borderRight: "none",
                },
                "&:not(:last-of-type)": {
      borderBottom: "none",
    },
            },
        }),
        muiTableHeadCellProps: ({ column }) => ({
            sx: {
                borderRight: column.getIsLastColumn() ? undefined : "1px solid white",
                color: "rgb(255 255 255)",
                backgroundColor: "#042a4c",
                position: "sticky",
                borderRight: "1px solid lightgray",
                "&:last-child": {
                    borderRight: "none",
                },
                borderBottom: "none",
               
            },
        }),
 
        renderRowActions: ({ row, table }) => (
            <Box sx={{ display: "flex", gap: "1rem" }}>
                <Tooltip title="Edit">
                    <IconButton onClick={() => table.setEditingRow(row)}>
                        <EditIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                    <IconButton
                        color="error"
                        onClick={() => handleDeleteBtnClick(row?.index, true)}
 
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
        renderTopToolbarCustomActions: ({ table }) => (
            <div className="mt-1">
                <Button
                    startIcon={<Icon icon={ICONS.ADD} />}
                    onClick={() => {
                        const emptyRow = createRow(table, {});
                        table.setCreatingRow(emptyRow);
                    }}
                    disabled={!!table.getState().creatingRow}
                >
                    Add WASP
                </Button>
            </div>
        ),
    });
 
    return (
        <>
            <div className="fuel-registry-b7 mt-1">
                <MaterialReactTable table={table} />
            </div>
            <div className="confirmation-modal-container">
                <Modal
                    isOpen={confirmDelete}
                    modalHeader
                    padding={"70px"}
                    headerHeight={"50px"}
                    showFooter
                    onCloseClickListener={() => {
                        setConfirmDelete(false);
                    }}
                    footer={
                        <div className="d-flex justify-content-end fixed-action-btns p-4">
                            <Button
                                className="modal-delete-btn"
                                // onClick={() => { handleDeleteFuel(deletingRowIndex) }}
                                onClick={
                                    async () => {
                                        const updated = fuelWindData.filter((_, i) => i !== deletingRowIndex);
                                        await handleSave(updated, table, true);
                                        emitToasterMsg("success", "Table B.7", "Record deleted successfully");
                                    }
                                }
                                primaryColor={COLORS.RED.DARK}
                                primaryBackgroundColor={COLORS.RED.LIGHT}
                            >
                                Delete
                            </Button>
                            <Button
                                className="modal-cancel-btn"
                                onClick={() => {
                                    setConfirmDelete(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    }
                >
                    <div>Are you sure you want to delete this WASP?</div>
                </Modal>
            </div>
        </>
    );
});
 
export default FeuB7WindAssistedPropulsion;